'use client';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageProvider';

const ACCENT = '#d6342c';
const HAIRLINE = 'rgba(255, 248, 240, 0.12)';
type Lang = 'CN' | 'EN' | 'DE';
function useV2Lang(): Lang {
  const { language } = useLanguage();
  return (language ?? 'EN') as Lang;
}

/**
 * Cookie consent banner.
 *
 * Why: PathIN e.V. is a German-registered nonprofit; GDPR applies. Even if
 * the site currently uses only essential cookies, an explicit consent UI
 * signals compliance to visitors and reserves the option to add analytics later.
 *
 * Storage: a single localStorage key `pathin_consent_v1` holding
 * `{ state: 'accepted' | 'rejected', ts: ISO }`. Bumping the version
 * (`_v1` → `_v2`) re-prompts everyone, useful if cookie scope changes.
 */

const STORAGE_KEY = 'pathin_consent_v1';

type Consent = { state: 'accepted' | 'rejected'; ts: string };

const COPY = {
  CN: {
    title: '关于 Cookie',
    body: '本站使用必要的功能性 Cookie 让网站正常运作（如语言偏好）。在您同意后，我们也可能用 Cookie 了解匿名访问情况以改进内容。详见',
    privacy: '隐私政策',
    accept: '全部接受',
    reject: '仅必要',
  },
  EN: {
    title: 'About cookies',
    body: 'This site uses essential cookies for basic functionality (e.g. language preference). With your consent, we may also use cookies to understand anonymous visit patterns. See our',
    privacy: 'privacy policy',
    accept: 'Accept all',
    reject: 'Necessary only',
  },
  DE: {
    title: 'Über Cookies',
    body: 'Diese Website verwendet notwendige Cookies für grundlegende Funktionen (z. B. Sprachauswahl). Mit Ihrer Zustimmung nutzen wir Cookies auch, um anonyme Zugriffe zu verstehen. Siehe unsere',
    privacy: 'Datenschutzerklärung',
    accept: 'Alle akzeptieren',
    reject: 'Nur notwendige',
  },
};

export function CookieConsent() {
  const lang = useV2Lang();
  const t = COPY[lang];
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setTimeout(() => setVisible(true), 600);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const persist = (state: Consent['state']) => {
    try {
      const c: Consent = { state, ts: new Date().toISOString() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    } catch { /* localStorage unavailable; ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-4 md:px-10 pb-4 md:pb-6"
      role="dialog"
      aria-live="polite"
      aria-label={t.title}
    >
      <div
        className="mx-auto max-w-[1400px] backdrop-blur-md"
        style={{
          backgroundColor: 'rgba(12, 11, 10, 0.92)',
          border: `1px solid ${HAIRLINE}`,
          borderRadius: '4px',
        }}
      >
        <div className="px-5 md:px-8 py-5 md:py-6 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/55 mb-2">{t.title}</p>
            <p className="text-[13px] leading-[1.55] text-white/85 max-w-[68ch]">
              {t.body}{' '}
              <Link to="/datenschutz" className="underline underline-offset-4" style={{ color: ACCENT }}>
                {t.privacy}
              </Link>
              。
            </p>
          </div>
          <div className="flex flex-row gap-3 shrink-0">
            <button
              onClick={() => persist('rejected')}
              className="px-4 py-2 text-[13px] font-medium border hover:bg-white hover:text-black transition-colors whitespace-nowrap"
              style={{ borderColor: 'rgba(255,255,255,0.4)' }}
            >
              {t.reject}
            </button>
            <button
              onClick={() => persist('accepted')}
              className="px-4 py-2 text-[13px] font-semibold text-black hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{ backgroundColor: ACCENT, color: '#fff' }}
            >
              {t.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function getConsentState(): Consent['state'] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as Consent).state;
  } catch {
    return null;
  }
}
